import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate, returnSign } from 'helpers/helpers'

/**
 * «УМУМИЙ РАСХОД» — GURUHLANGAN KO'RINISH (2026-08-06)
 *
 * Uch daraja, har biri bosilganda ostida ochiladi:
 *   1. Guruh    (Операцион, Маъмурий, ...)  — `/expenses-grouped` dan
 *   2. Modda    (Obed uchun, Yol kira, ...) — o'sha javobning ichida
 *   3. Tranzaksiya                          — `/expenses?payment_purpose_id=` dan, KERAK BO'LGANDA
 *
 * NEGA 1 va 2 birdaniga: moddalar soni kichik (eng katta tenantda 131 ta),
 * shuning uchun guruh ochilishi bir zumda bo'ladi, so'rov kutilmaydi.
 * Tranzaksiyalar esa minglab bo'lishi mumkin — ular faqat so'ralganda olinadi.
 *
 * ⚠️ «Тасниф қилинмаган» guruhi TASHLAB YUBORILMAYDI — eng tepada, qizil holda
 *    turadi. Aks holda tasniflanmagan pul jimgina yo'qolgandek bo'lardi.
 *
 * ⚠️ O'chirish tugmasi FAQAT 3-darajada (tranzaksiya) — guruh yoki moddani
 *    o'chirish degan tushuncha yo'q.
 */

/**
 * Guruh ranglari. Guruhlar endi foydalanuvchi tomonidan yaratiladi, ya'ni
 * ro'yxat oldindan noma'lum — shuning uchun rang guruh `id` si bo'yicha
 * doimiy tarzda tanlanadi (bir guruh doim bir xil rangda ko'rinadi).
 */
const PALETTE = [
	{ background: '#e8effc', color: '#2456a6' },
	{ background: '#f0eafc', color: '#5b34a8' },
	{ background: '#e4f4f4', color: '#12706e' },
	{ background: '#fdefe3', color: '#a35a12' },
	{ background: '#fdeaea', color: '#a32d2d' },
	{ background: '#e8f5ec', color: '#1c7340' },
	{ background: '#eef0f3', color: '#5d6674' },
	{ background: '#f2f3f5', color: '#7b8494' },
]
const UNCLASSIFIED_STYLE = { background: '#fff2f2', color: '#c22a2a', border: '1px dashed #f0a9a9' }

function ExpenseGroups({ filterData, onDelete, reloadKey }) {
	const { t } = useTranslation()

	const [groups, setGroups] = useState([])
	const [totals, setTotals] = useState({ total: 0, total_expense: 0, total_neutral: 0 })
	const [openGroups, setOpenGroups] = useState({})     // guruh kodi -> ochiqmi
	const [openPurposes, setOpenPurposes] = useState({}) // modda id -> ochiqmi
	const [rowsByPurpose, setRowsByPurpose] = useState({}) // modda id -> tranzaksiyalar
	const [loadingPurpose, setLoadingPurpose] = useState(null)

	const groupName = (g) => g.name || t('pl_unclassified')
	const groupStyle = (g) => g.id ? PALETTE[g.id % PALETTE.length] : UNCLASSIFIED_STYLE

	/** Filtrni URL ga o'giradi — `Rasxodlar.js` dagi bilan bir xil maydonlar */
	function filterParams() {
		var p = ''
		if (filterData.start_date) p += returnSign(p) + 'start_date=' + filterData.start_date
		if (filterData.end_date) p += returnSign(p) + 'end_date=' + filterData.end_date
		if (filterData.created_by) p += returnSign(p) + 'created_by=' + filterData.created_by
		if (filterData.payment_type_id) p += returnSign(p) + 'payment_type_id=' + filterData.payment_type_id
		if (filterData.transaction_type) p += returnSign(p) + 'transaction_type=' + filterData.transaction_type
		if (filterData.payment_purpose_id) p += returnSign(p) + 'payment_purpose_id=' + filterData.payment_purpose_id
		return p
	}

	async function load() {
		const response = await GET('/expenses-grouped' + filterParams(), {}, { loader: true })
		if (httpOk(response)) {
			setGroups(response.data.groups || [])
			setTotals({
				total: response.data.total || 0,
				total_expense: response.data.total_expense || 0,
				total_neutral: response.data.total_neutral || 0,
			})
			// Filtr o'zgarsa ochilganlar eskirib qoladi — tozalaymiz
			setRowsByPurpose({})
			setOpenPurposes({})
		}
	}

	useEffect(() => { load() },
		[filterData.start_date, filterData.end_date, filterData.created_by,
		filterData.payment_type_id, filterData.transaction_type,
		filterData.payment_purpose_id, reloadKey]) // eslint-disable-line react-hooks/exhaustive-deps

	async function togglePurpose(purposeId) {
		if (openPurposes[purposeId]) {
			setOpenPurposes(p => ({ ...p, [purposeId]: false }))
			return
		}
		setOpenPurposes(p => ({ ...p, [purposeId]: true }))
		if (rowsByPurpose[purposeId]) return   // bir marta olingan, qayta so'ramaymiz

		setLoadingPurpose(purposeId)
		var p = filterParams()
		// Moddaning O'ZI bo'yicha — umumiy filtrdagi modda bo'lsa ham ustidan yozamiz
		p = p.replace(/([?&])payment_purpose_id=[^&]*/, '$1_x=1')
		p += returnSign(p) + 'payment_purpose_id=' + purposeId
		p += returnSign(p) + 'size=1000'
		const response = await GET('/expenses' + p, {}, { loader: false })
		if (httpOk(response)) {
			setRowsByPurpose(prev => ({ ...prev, [purposeId]: response.data.data || [] }))
		}
		setLoadingPurpose(null)
	}

	const allOpen = groups.length > 0 && groups.every(g => openGroups[g.id || '__none__'])
	function toggleAll() {
		if (allOpen) { setOpenGroups({}); return }
		const next = {}
		groups.forEach(g => { next[g.id || '__none__'] = true })
		setOpenGroups(next)
	}

	// Ikki blok: foydadan ayiriladigan va tegmaydigan.
	// Tasniflanmagan hech qaysisiga kirmaydi — u alohida, tepada.
	const unclassified = groups.filter(g => !g.id)
	const expenseGroups = groups.filter(g => g.id && g.effect === 'expense')
	const neutralGroups = groups.filter(g => g.id && g.effect !== 'expense')

	function renderGroup(g) {
		const key = g.id || '__none__'
		const open = !!openGroups[key]
		return (
			<React.Fragment key={key}>
				{/* ⚠️ Rang CSS o'zgaruvchisi orqali: `--dk-*` faqat qorong'i mavzuda
				    e'lon qilingan, yoruqda esa zaxira qiymat ishlaydi — ya'ni yoruq
				    ko'rinish O'ZGARMAYDI. Ilgari `#f7f8fb` qotirilgan edi va qorong'ida
				    guruh qatori o'qilmas holga kelardi (foydalanuvchi surati). */}
				<tr style={{ background: 'var(--dk-surface-3, #f7f8fb)', cursor: 'pointer' }}
					onClick={() => setOpenGroups(p => ({ ...p, [key]: !p[key] }))}>
					<td colSpan="2" className="fw-bold">
						<span className="text-muted me-1" style={{ fontSize: '11px' }}>{open ? '▼' : '►'}</span>
						<span className="badge" style={groupStyle(g)}>{groupName(g)}</span>
						<span className="text-muted ms-2" style={{ fontWeight: 400 }}>
							— {g.purposes.length} {t('pl_purposes')}
						</span>
					</td>
					<td className="text-center text-muted">{g.cnt}</td>
					<td className="text-end fw-bold text-nowrap">{formatMoney(g.total)}</td>
					<td></td>
				</tr>

				{open && g.purposes.map(p => (
					<React.Fragment key={String(key) + '-' + String(p.id)}>
						<tr style={{ cursor: p.id ? 'pointer' : 'default' }}
							onClick={() => p.id && togglePurpose(p.id)}>
							<td style={{ paddingLeft: '34px' }} colSpan="2">
								{p.id &&
									<span className="text-muted me-1" style={{ fontSize: '11px' }}>
										{openPurposes[p.id] ? '▼' : '►'}
									</span>}
								{p.name}
								{loadingPurpose === p.id &&
									<span className="text-muted ms-2" style={{ fontSize: '12px' }}>…</span>}
							</td>
							<td className="text-center text-muted">{p.cnt}</td>
							<td className={'text-end text-nowrap ' + (p.transaction_type === 'plus' ? 'text-success' : '')}>
								{formatMoney(p.total)}
							</td>
							<td></td>
						</tr>

						{openPurposes[p.id] && (rowsByPurpose[p.id] || []).map((row) => (
							<tr key={'r' + row.id} style={{ background: 'var(--dk-surface, #fcfdfe)' }}>
								<td style={{ paddingLeft: '64px' }} className="td-ellipsis text-muted">
									{row.created_by} · {row.payment_name}
								</td>
								<td className="td-ellipsis text-muted" title={row.note}>{row.note}</td>
								<td className="text-center text-nowrap text-muted" style={{ fontSize: '12.5px' }}>
									{formatDate(row.created_at, 'dd.MM.yyyy HH:mm')}
								</td>
								<td className={'text-end text-nowrap ' + (row.transaction_type === 'plus' ? 'text-success' : 'text-danger')}>
									{formatMoney(row.amount)}
								</td>
								<td className="text-center">
									<i className="uil-trash text-danger cursor" style={{ fontSize: '15px' }}
										onClick={(e) => { e.stopPropagation(); onDelete(row) }} />
								</td>
							</tr>
						))}

						{openPurposes[p.id] && rowsByPurpose[p.id] && rowsByPurpose[p.id].length === 0 &&
							<tr><td colSpan="5" className="text-center text-muted py-2" style={{ fontSize: '12.5px' }}>
								{t('list_empty')}
							</td></tr>}
					</React.Fragment>
				))}
			</React.Fragment>
		)
	}

	return (
		<>
			<div className="d-flex justify-content-end mb-2">
				<button className="btn btn-outline-primary btn-sm" onClick={toggleAll} disabled={!groups.length}>
					{allOpen ? '▲ ' : '▼ '}{allOpen ? t('close') : t('open')}
				</button>
			</div>

			<div className="table-responsive">
				<table className="table table-bordered mb-0">
					<thead>
						<tr>
							<th style={{ width: '38%' }}>{t('pl_group')} / {t('type')}</th>
							<th style={{ width: '22%' }}>{t('note')}</th>
							<th style={{ width: '10%' }} className="text-center">{t('quantity')}</th>
							<th style={{ width: '15%' }} className="text-end">{t('amount')}</th>
							<th style={{ minWidth: '33px' }}></th>
						</tr>
					</thead>
					<tbody>
						{groups.length === 0 ? (
							<tr><td colSpan="5" className="text-center text-muted py-4">{t('list_empty')}</td></tr>
						) : (
							<>
								{unclassified.map(renderGroup)}

								{expenseGroups.length > 0 &&
									<tr><td colSpan="5" className="fw-bold text-muted"
										style={{ background: '#fcfdfe', fontSize: '12px', letterSpacing: '.05em' }}>
										{t('pl_subtracted')}
									</td></tr>}
								{expenseGroups.map(renderGroup)}
								{expenseGroups.length > 0 &&
									<tr className="tr-bold table-secondary">
										<td colSpan="3" className="fw-bold">{t('pl_total_expense')}</td>
										<td className="text-end fw-bold text-nowrap">{formatMoney(totals.total_expense)}</td>
										<td></td>
									</tr>}

								{neutralGroups.length > 0 &&
									<tr><td colSpan="5" className="fw-bold text-muted"
										style={{ background: '#fcfdfe', fontSize: '12px', letterSpacing: '.05em' }}>
										{t('pl_not_subtracted')}
									</td></tr>}
								{neutralGroups.map(renderGroup)}

								{/* Ikkala blok + tasniflanmagan = kassadan chiqqan haqiqiy pul.
								    Faqat bitta blok bo'lsa yuqoridagi jami bilan bir xil bo'lardi —
								    ikkita ayni raqamni ketma-ket ko'rsatish chalkashtiradi. */}
								{Math.round(totals.total) !== Math.round(totals.total_expense) &&
									<tr className="tr-bold table-secondary">
										<td colSpan="3" className="fw-bold">{t('pl_total_cash')}</td>
										<td className="text-end fw-bold text-nowrap">{formatMoney(totals.total)}</td>
										<td></td>
									</tr>}
							</>
						)}
					</tbody>
				</table>
			</div>
		</>
	)
}

export default ExpenseGroups
